<template>
  <FullCalendar ref="calendarRef" :options="calendarOptions">
    <template v-slot:eventContent="arg">
      <div
        :class="getBackground(arg.event)"
        class="d-flex flex-row justify-content-between w-100 px-1 cursor-pointer"
        :id="arg.event.id"
        @click="openModal(arg.event.extendedProps)"
      >
        <p class="font-weight-bold">
          {{ arg.timeText }}
        </p>
        <p>
          {{ arg.event.title }}
        </p>
      </div>
    </template>
  </FullCalendar>
  <CalendarEventModal :show="showModal" :selectedEvent="selectedEvent" @close="showModal = false" />
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import frLocale from '@fullcalendar/core/locales/fr'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import { useIntersectionObserver } from '@vueuse/core'
import { defineComponent, reactive, ref, watch, toRef } from 'vue'
import CalendarEventModal from '@components/concierge/CalendarEventModal.vue'
import { wTrans, getActiveLanguage } from 'laravel-vue-i18n'
import { usePage } from '@inertiajs/vue3'

export default defineComponent({
  components: {
    FullCalendar,
    CalendarEventModal
  },
  props: {
    allEvents: {
      type: Array,
      default() {
        return []
      }
    },
    testDriveTypes: {
      type: Array,
      default() {
        return []
      }
    },
    meetingTypes: {
      type: Array,
      default() {
        return []
      }
    },
    businessHours: {
      type: Array,
      default() {
        return []
      }
    },
    weekLength: {
      type: Number,
      default: 7
    }
  },
  setup() {
    const page = usePage()
    const timezone = ref(page.props.timezone || 'America/Montreal')
    const calendarRefIsVisible = ref(false)
    const calendarRef = ref(null)
    const showModal = ref(false)
    const selectedEvent = reactive({})

    useIntersectionObserver(calendarRef, ([{ isIntersecting }], observerElement) => {
      calendarRefIsVisible.value = isIntersecting
    })

    watch(calendarRefIsVisible, () => {
      if (calendarRefIsVisible.value) {
        const calendarApi = calendarRef.value.getApi()
        calendarApi.updateSize()
        let hasActiveFilter = false
        calendarApi.el.querySelectorAll('.fc-toolbar-chunk:first-child .btn').forEach(button => {
          if (button.className.search('active') > -1) {
            hasActiveFilter = true
          }
        })
        if (!hasActiveFilter) {
          calendarApi.el.querySelectorAll('.fc-toolbar-chunk:first-child .btn').forEach(button => {
            if (button.className.search('reset') > -1) {
              button.classList.add('active')
            }
          })
        }
      }
    })

    return { calendarRef, showModal, selectedEvent, timezone }
  },
  data() {
    return {
      calendarOptions: reactive({
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, bootstrapPlugin],
        locales: [frLocale],
        locale: getActiveLanguage(),
        timeZone: this.timezone,
        businessHours: this.businessHours,
        headerToolbar: {
          left: 'testDrive,adviserAppointment,reset',
          center: 'title',
          right: 'prev,next today dayGridMonth,timeGridWeek,timeGridDay'
        },
        themeSystem: 'bootstrap',
        duration: { days: this.weekLength },
        weekends: this.weekLength > 5,
        dayHeaderFormat: { weekday: 'long' },
        initialView: 'dayGridMonth',
        initialEvents: this.allEvents,
        editable: false,
        selectMirror: true,
        dayMaxEvents: true,
        slotMinTime: '05:00:00',
        slotMaxTime: '23:30:00',
        expandRows: true,
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
          hour12: false
        },
        eventClick: this.openModal,
        customButtons: {
          reset: {
            text: wTrans('services/concierge_dashboard.calendar_filters.all'),
            click: (ev, element) => {
              const calendar = this.calendarRef.getApi()
              let events = calendar.getEvents()
              this.toggleFilterButtons(element.parentNode.children, 'reset')
              for (const event of events) {
                event.setProp('display', 'auto')
              }
            }
          },
          testDrive: {
            text: wTrans('services/concierge_dashboard.calendar_filters.test_drives'),
            click: (ev, element) => {
              const calendar = this.calendarRef.getApi()
              let events = calendar.getEvents()
              this.toggleFilterButtons(element.parentNode.children, 'testDrive')
              for (const event of events) {
                const { extendedProps } = event
                if (Object.hasOwn(extendedProps, 'type')) {
                  if (this.isTestDriveType(extendedProps?.type)) {
                    event.setProp('display', 'auto')
                  } else {
                    event.setProp('display', 'none')
                  }
                }
              }
            }
          },
          adviserAppointment: {
            text: wTrans('services/concierge_dashboard.calendar_filters.adviser_appointments'),
            click: (ev, element) => {
              const calendar = this.calendarRef.getApi()
              let events = calendar.getEvents()
              this.toggleFilterButtons(element.parentNode.children, 'adviserAppointment')
              for (const event of events) {
                const { extendedProps } = event
                if (Object.hasOwn(extendedProps, 'type')) {
                  if (this.isMeetingType(extendedProps?.type)) {
                    event.setProp('display', 'auto')
                  } else {
                    event.setProp('display', 'none')
                  }
                }
              }
            }
          }
        }
      })
    }
  },
  watch: {
    allEvents: {
      handler(val, oldVal) {
        this.calendarOptions.events = val
      },
      deep: true
    }
  },
  methods: {
    getBackground(event) {
      const { extendedProps } = event

      if (Object.hasOwn(extendedProps, 'type')) {
        const type = extendedProps.type
        if (this.isMeetingType(type)) {
          return 'bg-blue-dark'
        } else if (this.isTestDriveType(type)) {
          return 'bg-danger'
        }
      }

      return 'bg-light'
    },
    isMeetingType(type) {
      return this.meetingTypes.includes(type)
    },
    isTestDriveType(type) {
      return this.testDriveTypes.includes(type)
    },
    toggleFilterButtons(elements, filter) {
      for (const button of elements) {
        button.classList.remove('active')
        if (button.className.search(filter) > 0) {
          button.classList.add('active')
        }
      }
    },
    openModal(event) {
      this.selectedEvent = event
      this.showModal = true
    }
  }
})
</script>

<style lang="scss">
.fc-header-toolbar.fc-toolbar {
  padding-right: 0;
  padding-left: 0;
}

.fc-reset-button {
  order: 1;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 4px !important;
  border-bottom-left-radius: 4px !important;
}

.fc-testDrive-button {
  order: 2;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.fc-adviserAppointment-button {
  order: 3;
  border-top-right-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
